import { useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, CheckCircle, CircleAlert, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useCreateRelativeAdjustment,
  useCreatePhysicalCount,
} from "@/hooks/useStockTransactions";
import { useLocation } from "wouter";
import type { Item } from "@/types/item";
import { SearchStockModal } from "@/components/modal-stock";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

// Schema for Mode 1: Relative Adjustment
const relativeAdjustmentSchema = z.object({
  header: z.object({
    voucherDate: z.string().min(1, "Date is required"),
    reference: z.string().optional(),
    reason: z.string().min(1, "Reason is required"),
  }),
  details: z
    .array(
      z.object({
        itemId: z.string().min(1, "Please select an item"),
        itemCode: z.string().optional(),
        description: z.string().optional(),
        currentQty: z.number().optional(),
        currentWA: z.number().optional(),
        adjustmentQty: z.number().refine((val) => val !== 0, {
          message: "Adjustment quantity cannot be zero",
        }),
        updateCost: z.boolean().default(false),
        unitCost: z.number().nonnegative("Unit cost cannot be negative").optional(),
      })
    )
    .min(1, "At least one item is required"),
});

// Schema for Mode 2: Physical Count
const physicalCountSchema = z.object({
  header: z.object({
    voucherDate: z.string().min(1, "Date is required"),
    reference: z.string().optional(),
    reason: z.string().min(1, "Reason is required"),
  }),
  details: z
    .array(
      z.object({
        itemId: z.string().min(1, "Please select an item"),
        itemCode: z.string().optional(),
        description: z.string().optional(),
        systemQty: z.number().optional(),
        currentWA: z.number().optional(),
        countedQty: z.number().nonnegative("Counted quantity cannot be negative"),
        updateWA: z.boolean().default(false),
        newWeightedAverage: z
          .number()
          .nonnegative("New weighted average cannot be negative")
          .optional(),
      })
    )
    .min(1, "At least one item is required"),
});

type RelativeAdjustmentFormValues = z.infer<typeof relativeAdjustmentSchema>;
type PhysicalCountFormValues = z.infer<typeof physicalCountSchema>;

export const StockAdjustment = () => {
  const { format: formatDecimal, step } = useDecimalPrecision();
  const { toast } = useToast();
  const [_, navigate] = useLocation();
  const [adjustmentMode, setAdjustmentMode] = useState<"relative" | "physical">(
    "relative"
  );
  const createRelativeAdjustment = useCreateRelativeAdjustment();
  const createPhysicalCount = useCreatePhysicalCount();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  // Relative adjustment form
  const relativeForm = useForm<RelativeAdjustmentFormValues>({
    resolver: zodResolver(relativeAdjustmentSchema),
    defaultValues: {
      header: {
        voucherDate: format(new Date(), "yyyy-MM-dd"),
        reference: "",
        reason: "",
      },
      details: [],
    },
  });

  // Physical count form
  const physicalForm = useForm<PhysicalCountFormValues>({
    resolver: zodResolver(physicalCountSchema),
    defaultValues: {
      header: {
        voucherDate: format(new Date(), "yyyy-MM-dd"),
        reference: "",
        reason: "",
      },
      details: [],
    },
  });

  const relativeFields = useFieldArray({
    control: relativeForm.control,
    name: "details",
  });

  const physicalFields = useFieldArray({
    control: physicalForm.control,
    name: "details",
  });

  const handleAddItem = (item: Item) => {
    // Check if item already exists
    const relativeDetails = relativeForm.watch("details");
    const physicalDetails = physicalForm.watch("details");
    const allDetails = adjustmentMode === "relative" ? relativeDetails : physicalDetails;
    const exists = allDetails.some((d: any) => d.itemId === item.id);
    if (exists) {
      toast({
        title: "Item Already Added",
        description: "This item is already in the list",
        variant: "destructive",
        duration: 4000,
      });
      return;
    }

    if (adjustmentMode === "relative") {
      relativeFields.append({
        itemId: item.id,
        itemCode: item.itemCode,
        description: item.description,
        currentQty: item.quantity || 0,
        currentWA: item.weightedAverage || 0,
        adjustmentQty: 0,
        updateCost: false,
        unitCost: item.weightedAverage || 0,
      });
    } else {
      physicalFields.append({
        itemId: item.id,
        itemCode: item.itemCode,
        description: item.description,
        systemQty: item.quantity || 0,
        currentWA: item.weightedAverage || 0,
        countedQty: item.quantity || 0,
        updateWA: false,
        newWeightedAverage: item.weightedAverage || 0,
      });
    }
  };

  const onSubmitRelative = async (data: RelativeAdjustmentFormValues) => {
    try {
      await createRelativeAdjustment.mutateAsync(
        {
          reference: data.header.reference,
          entryDate: data.header.voucherDate,
          reason: data.header.reason,
          items: data.details.map((item) => ({
            stockCode: item.itemId,
            adjustmentQty: item.adjustmentQty,
            unitCost: item.updateCost ? item.unitCost : undefined,
          })),
        },
        {
          onSuccess: () => {
            toast({
              title: "Stock Adjustment Created",
              description: `Relative adjustment created successfully`,
              icon: (
                <CheckCircle
                  size={30}
                  className="animate-spin-once text-green-400"
                />
              ),
              duration: 5000,
            });
            navigate("/stock-transactions");
          },
          onError: (error: any) => {
            toast({
              title: "Failed to Create Adjustment",
              description: error.response?.data?.message || error.message,
              variant: "destructive",
              icon: <CircleAlert size={30} className="animate-spin-once" />,
              duration: 4000,
            });
          },
        }
      );
    } catch (error) {
      console.error("Error creating relative adjustment:", error);
    }
  };

  const onSubmitPhysical = async (data: PhysicalCountFormValues) => {
    try {
      await createPhysicalCount.mutateAsync(
        {
          reference: data.header.reference,
          entryDate: data.header.voucherDate,
          reason: data.header.reason,
          items: data.details.map((item) => ({
            stockCode: item.itemId,
            countedQty: item.countedQty,
            newWeightedAverage: item.updateWA ? item.newWeightedAverage : undefined,
          })),
        },
        {
          onSuccess: () => {
            toast({
              title: "Physical Count Created",
              description: `Physical count adjustment created successfully`,
              icon: (
                <CheckCircle
                  size={30}
                  className="animate-spin-once text-green-400"
                />
              ),
              duration: 5000,
            });
            navigate("/stock-transactions");
          },
          onError: (error: any) => {
            toast({
              title: "Failed to Create Physical Count",
              description: error.response?.data?.message || error.message,
              variant: "destructive",
              icon: <CircleAlert size={30} className="animate-spin-once" />,
              duration: 4000,
            });
          },
        }
      );
    } catch (error) {
      console.error("Error creating physical count:", error);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Stock Adjustment</h1>
        <Button variant="outline" onClick={() => navigate("/stock-transactions")}>
          Back to List
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {/* Mode Selection */}
          <div className="space-y-4 mb-6">
            <h3 className="text-lg font-semibold">Adjustment Mode</h3>
            <RadioGroup
              value={adjustmentMode}
              onValueChange={(value) => setAdjustmentMode(value as "relative" | "physical")}
              className="space-y-3"
            >
              <div className="flex items-start space-x-3 p-4 border rounded-lg">
                <RadioGroupItem value="relative" id="relative" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="relative" className="text-base font-semibold cursor-pointer">
                    Mode 1: Relative Adjustment (± Quantity)
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add or subtract quantity from current stock. Optionally update cost.
                    Use for damaged goods, found inventory, etc.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 border rounded-lg">
                <RadioGroupItem value="physical" id="physical" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="physical" className="text-base font-semibold cursor-pointer">
                    Mode 2: Physical Count (Absolute Quantity)
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Zero-out current stock and set to counted quantity. Optionally update weighted average.
                    Use for stocktakes and physical counts.
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Relative Adjustment Form */}
          {adjustmentMode === "relative" && (
            <form onSubmit={relativeForm.handleSubmit(onSubmitRelative)} className="space-y-6">
              {/* Header Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Adjustment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="voucherDate">
                      Date <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      type="date"
                      {...relativeForm.register("header.voucherDate")}
                      className="h-10"
                    />
                    {relativeForm.formState.errors.header?.voucherDate && (
                      <p className="text-red-600 text-sm mt-1">
                        {relativeForm.formState.errors.header.voucherDate.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="reference">Reference</Label>
                    <Input
                      {...relativeForm.register("header.reference")}
                      placeholder="e.g., ADJ-2025-01"
                      className="h-10"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="reason">
                      Reason <span className="text-red-600">*</span>
                    </Label>
                    <Textarea
                      {...relativeForm.register("header.reason")}
                      placeholder="Explain the reason for this adjustment"
                      className="min-h-20"
                    />
                    {relativeForm.formState.errors.header?.reason && (
                      <p className="text-red-600 text-sm mt-1">
                        {relativeForm.formState.errors.header.reason.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Line Items Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    Items <span className="text-red-600">*</span>
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsSearchModalOpen(true)}
                    className="shadow"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>

                {relativeForm.formState.errors.details?.root && (
                  <p className="text-red-600 text-sm">{relativeForm.formState.errors.details.root.message}</p>
                )}

                {relativeFields.fields.length > 0 && (
                  <Table>
                    <TableHeader className="bg-slate-50 dark:bg-slate-800">
                      <TableRow>
                        <TableHead>Item Code</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-28">Current Qty</TableHead>
                        <TableHead className="w-32">Adjustment (±)</TableHead>
                        <TableHead className="w-28">New Qty</TableHead>
                        <TableHead className="w-24">Update Cost?</TableHead>
                        <TableHead className="w-32">Unit Cost (RM)</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {relativeFields.fields.map((field, index) => {
                        const item = relativeForm.watch("details")[index];
                        const newQty = (item?.currentQty || 0) + (item?.adjustmentQty || 0);
                        return (
                          <TableRow key={field.id}>
                            <TableCell className="font-medium">
                              {item?.itemCode}
                            </TableCell>
                            <TableCell>{item?.description}</TableCell>
                            <TableCell className="text-center">
                              {formatDecimal(item?.currentQty)}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step={step}
                                {...relativeForm.register(`details.${index}.adjustmentQty`, {
                                  valueAsNumber: true,
                                })}
                                className="h-8"
                              />
                              {relativeForm.formState.errors.details?.[index]?.adjustmentQty && (
                                <p className="text-red-600 text-xs mt-1">
                                  {relativeForm.formState.errors.details[index]?.adjustmentQty?.message}
                                </p>
                              )}
                            </TableCell>
                            <TableCell className={`text-center font-medium ${newQty < 0 ? 'text-red-600' : ''}`}>
                              {formatDecimal(newQty)}
                            </TableCell>
                            <TableCell className="text-center">
                              <Controller
                                name={`details.${index}.updateCost`}
                                control={relativeForm.control}
                                render={({ field }) => (
                                  <Checkbox
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step={step}
                                {...relativeForm.register(`details.${index}.unitCost`, {
                                  valueAsNumber: true,
                                })}
                                disabled={!item?.updateCost}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => relativeFields.remove(index)}
                                className="h-8 w-8 p-0 text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}

                {relativeFields.fields.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground border rounded-md">
                    No items added. Search and select items to add them to the adjustment.
                  </div>
                )}
              </div>

              {relativeFields.fields.length > 0 && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>About Relative Adjustments</AlertTitle>
                  <AlertDescription className="text-sm space-y-1">
                    <p>• Positive values add to stock, negative values subtract</p>
                    <p>• If "Update Cost" is unchecked, current weighted average will be used</p>
                    <p>• If "Update Cost" is checked, the specified cost will be used for WA calculation</p>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-4 pt-4">
                <Button
                  type="submit"
                  className="w-48 h-10 text-base"
                  disabled={createRelativeAdjustment.isPending}
                >
                  {createRelativeAdjustment.isPending
                    ? "Creating..."
                    : "Create Adjustment"}
                </Button>
              </div>
            </form>
          )}

          {/* Physical Count Form */}
          {adjustmentMode === "physical" && (
            <form onSubmit={physicalForm.handleSubmit(onSubmitPhysical)} className="space-y-6">
              {/* Header Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Adjustment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="voucherDate">
                      Date <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      type="date"
                      {...physicalForm.register("header.voucherDate")}
                      className="h-10"
                    />
                    {physicalForm.formState.errors.header?.voucherDate && (
                      <p className="text-red-600 text-sm mt-1">
                        {physicalForm.formState.errors.header.voucherDate.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="reference">Reference</Label>
                    <Input
                      {...physicalForm.register("header.reference")}
                      placeholder="e.g., Count-2025-01"
                      className="h-10"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="reason">
                      Reason <span className="text-red-600">*</span>
                    </Label>
                    <Textarea
                      {...physicalForm.register("header.reason")}
                      placeholder="Explain the reason for this physical count"
                      className="min-h-20"
                    />
                    {physicalForm.formState.errors.header?.reason && (
                      <p className="text-red-600 text-sm mt-1">
                        {physicalForm.formState.errors.header.reason.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Line Items Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    Items <span className="text-red-600">*</span>
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsSearchModalOpen(true)}
                    className="shadow"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>

                {physicalForm.formState.errors.details?.root && (
                  <p className="text-red-600 text-sm">{physicalForm.formState.errors.details.root.message}</p>
                )}

                {physicalFields.fields.length > 0 && (
                  <Table>
                    <TableHeader className="bg-slate-50 dark:bg-slate-800">
                      <TableRow>
                        <TableHead>Item Code</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-28">System Qty</TableHead>
                        <TableHead className="w-32">Counted Qty</TableHead>
                        <TableHead className="w-28">Variance</TableHead>
                        <TableHead className="w-24">Update WA?</TableHead>
                        <TableHead className="w-32">New WA (RM)</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {physicalFields.fields.map((field, index) => {
                        const item = physicalForm.watch("details")[index];
                        const variance = (item?.countedQty || 0) - (item?.systemQty || 0);
                        return (
                          <TableRow key={field.id}>
                            <TableCell className="font-medium">
                              {item?.itemCode}
                            </TableCell>
                            <TableCell>{item?.description}</TableCell>
                            <TableCell className="text-center">
                              {formatDecimal(item?.systemQty)}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step={step}
                                {...physicalForm.register(`details.${index}.countedQty`, {
                                  valueAsNumber: true,
                                })}
                                className="h-8"
                              />
                              {physicalForm.formState.errors.details?.[index]?.countedQty && (
                                <p className="text-red-600 text-xs mt-1">
                                  {physicalForm.formState.errors.details[index]?.countedQty?.message}
                                </p>
                              )}
                            </TableCell>
                            <TableCell className={`text-center font-medium ${variance < 0 ? 'text-red-600' : variance > 0 ? 'text-green-600' : ''}`}>
                              {variance > 0 ? '+' : ''}{formatDecimal(variance)}
                            </TableCell>
                            <TableCell className="text-center">
                              <Controller
                                name={`details.${index}.updateWA`}
                                control={physicalForm.control}
                                render={({ field }) => (
                                  <Checkbox
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`details.${index}.newWeightedAverage`}
                                control={physicalForm.control}
                                render={({ field }) => (
                                  <Input
                                    type="number"
                                    step={step}
                                    value={field.value || ""}
                                    onChange={(e) => {
                                      const value = e.target.value === "" ? undefined : parseFloat(e.target.value);
                                      field.onChange(value);
                                    }}
                                    disabled={!item?.updateWA}
                                    className="h-8"
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => physicalFields.remove(index)}
                                className="h-8 w-8 p-0 text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}

                {physicalFields.fields.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground border rounded-md">
                    No items added. Search and select items to add them to the adjustment.
                  </div>
                )}
              </div>

              {physicalFields.fields.length > 0 && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>About Physical Count</AlertTitle>
                  <AlertDescription className="text-sm space-y-1">
                    <p>• System will zero-out current stock and set to your counted quantity</p>
                    <p>• If "Update WA" is unchecked, current weighted average will be preserved</p>
                    <p>• If "Update WA" is checked, the new weighted average will be applied</p>
                    <p>• A variance report will be generated showing discrepancies</p>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-4 pt-4">
                <Button
                  type="submit"
                  className="w-48 h-10 text-base"
                  disabled={createPhysicalCount.isPending}
                >
                  {createPhysicalCount.isPending
                    ? "Creating..."
                    : "Create Physical Count"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      <SearchStockModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        onSelectItem={handleAddItem}
        mode="in"
      />
    </div>
  );
};
